import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import type { FormGroup } from '../../hooks/useFieldForms';

interface FormGroupCardProps {
  group: FormGroup;
  showHeader: boolean;
}

export default function FormGroupCard({ group, showHeader }: FormGroupCardProps) {
  const navigate = useNavigate();

  /**
   * Why this row cannot be opened, or null if it can.
   *
   * Two causes, one treatment: a route nobody built (`comingSoon`) and a
   * template with no fields on it (`disabledReason`). Both would otherwise be a
   * tap that lands somewhere the user cannot make sense of — the catch-all home
   * screen in the first case, an empty page with a Submit button in the second.
   * The badge carries the reason so a greyed row is never just unresponsive.
   */
  const blockedReason = (form: FormGroup['forms'][number]): string | null =>
    form.comingSoon ? 'Coming soon' : form.disabledReason ?? null;

  const handleClick = (form: FormGroup['forms'][number]) => {
    const reason = blockedReason(form);
    if (reason) {
      toast.info(`${form.name} — ${reason.toLowerCase()}`);
      return;
    }
    navigate(form.route);
  };

  return (
    <section className="mb-5">
      {showHeader && (
        <p className="text-[11px] font-semibold uppercase tracking-wider text-fieldiq-text mb-2">
          {group.group}
        </p>
      )}
      <div className="rounded-xl border border-fieldiq-border bg-fieldiq-card divide-y divide-fieldiq-border">
        {group.forms.map((form) => {
          const blocked = blockedReason(form);
          return (
            <button
              key={form.id}
              onClick={() => handleClick(form)}
              className={`w-full flex items-center justify-between gap-3 p-3.5 text-left transition-colors ${
                blocked
                  ? 'opacity-50 cursor-default'
                  : 'hover:bg-fieldiq-card/30'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{form.name}</span>
                {blocked && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-fieldiq-text bg-fieldiq-card px-1.5 py-0.5 rounded">
                    {blocked}
                  </span>
                )}
              </div>
              {!blocked && <ChevronRight className="h-4 w-4 text-fieldiq-text" />}
            </button>
          );
        })}
      </div>
    </section>
  );
}
